import {useCallback, useEffect, useRef, useState} from 'react';

const SIZE = 48, MARGIN = 8;
const viewport = () => ({
  width: window.visualViewport?.width || window.innerWidth,
  height: window.visualViewport?.height || window.innerHeight,
});
const clamp = (n, min, max) => Math.max(min, Math.min(n, Math.max(min, max)));
const bounds = (point, view) => ({
  x: clamp(Number.isFinite(point?.x) ? point.x : view.width - SIZE - 20, MARGIN, view.width - SIZE - MARGIN),
  y: clamp(Number.isFinite(point?.y) ? point.y : view.height - SIZE - 20, MARGIN, view.height - SIZE - MARGIN),
});
const keyFor = identity => `fortunate:copilot-position:${identity}`;
function read(identity, view) {
  try {return bounds(JSON.parse(localStorage.getItem(keyFor(identity))), view);}
  catch {return bounds(null, view);}
}

export function useCopilotPosition(identity) {
  const [view, setView] = useState(viewport);
  const [saved, setSaved] = useState(() => ({identity, point: read(identity, viewport())}));
  const drag = useRef(null), suppressClick = useRef(false);
  const point = bounds(saved.identity === identity ? saved.point : read(identity, view), view);
  const pointRef = useRef(point); pointRef.current = point;

  const moveTo = useCallback(next => {
    const nextPoint = bounds(next, viewport());
    setSaved({identity, point: nextPoint});
    try {localStorage.setItem(keyFor(identity), JSON.stringify(nextPoint));} catch { /* Placement still works without storage. */ }
  }, [identity]);
  const reset = useCallback(() => moveTo(null), [moveTo]);
  useEffect(() => {
    const resize = () => {setView(viewport());};
    window.addEventListener('resize', resize);
    window.visualViewport?.addEventListener('resize', resize);
    return () => {window.removeEventListener('resize', resize);window.visualViewport?.removeEventListener('resize', resize);};
  }, []);
  useEffect(() => {drag.current=null;suppressClick.current=false;}, [identity]);

  const handlers = {
    onPointerDown(event) {
      if (event.button !== 0 || event.isPrimary === false) return;
      suppressClick.current = false;
      drag.current = {pointerId:event.pointerId, x:event.clientX, y:event.clientY, origin:pointRef.current, moved:false};
      event.currentTarget.setPointerCapture?.(event.pointerId);
    },
    onPointerMove(event) {
      const state=drag.current;
      if (!state || event.pointerId!==state.pointerId) return;
      const dx=event.clientX-state.x, dy=event.clientY-state.y;
      if (!state.moved && Math.hypot(dx,dy)<5) return;
      state.moved=true;
      event.preventDefault();
      setSaved({identity, point:bounds({x:state.origin.x+dx,y:state.origin.y+dy},viewport())});
    },
    onPointerUp(event) {
      const state=drag.current;
      if (!state || event.pointerId!==state.pointerId) return;
      if (state.moved) {
        suppressClick.current=true;
        moveTo({x:state.origin.x+event.clientX-state.x,y:state.origin.y+event.clientY-state.y});
      }
      drag.current=null;
      if (event.currentTarget.hasPointerCapture?.(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    },
    onPointerCancel() {if(drag.current) moveTo(drag.current.origin);drag.current=null;suppressClick.current=false;},
    onKeyDown(event) {
      const directions={ArrowLeft:[-1,0],ArrowRight:[1,0],ArrowUp:[0,-1],ArrowDown:[0,1]};
      if (event.key==='Home') {event.preventDefault();reset();}
      else if (directions[event.key]) {
        event.preventDefault();
        const [dx,dy]=directions[event.key],step=event.shiftKey?64:16;
        moveTo({x:pointRef.current.x+dx*step,y:pointRef.current.y+dy*step});
      }
    },
  };
  const panelWidth=Math.max(0,Math.min(380,view.width-2*MARGIN));
  const panelHeight=Math.max(0,Math.min(560,view.height-2*MARGIN));
  return {handlers,moveTo,reset,
    consumeDragClick:()=>{const wasDrag=suppressClick.current;suppressClick.current=false;return wasDrag;},
    launcherStyle:{left:point.x,top:point.y,touchAction:'none'},
    panelStyle:{width:panelWidth,height:panelHeight,
      left:clamp(point.x+SIZE-panelWidth,MARGIN,view.width-panelWidth-MARGIN),
      top:clamp(point.y+SIZE-panelHeight,MARGIN,view.height-panelHeight-MARGIN)},
  };
}
